<template>
  <section v-if="trader" class="flex flex-col h-full overflow-hidden">
    <BaseDetailsPreviewEditHeader
      :is-horizontal-layout="isHorizontalLayout"
      :title="modifiedTrader?.name ? modifiedTrader?.name : '-'"
      link-name="traders"
      :is-modified="isModified"
      :is-edit-active="trader.isFromManager && !trader.deleted"
      :mode="mode"
      @edit-mode="mode = 'edit'"
      @save="onSaveChanges"
      @cancel="onCancelEdit"
      @horizontal-layout="(value: boolean) => (isHorizontalLayout = value)" />

    <AppResizableContainer :layout="isHorizontalLayout ? 'vertical' : 'horizontal'" :minSize="225">
      <template #headerContent>
        <!-- Horizontal Layout -->
        <PerfectScrollbar class="w-full h-full py-4 space-y-2" ref="scrollbarId">
          <TraderDetailsHeader
            v-model="modifiedTrader"
            :mode="mode"
            :is-horizontal-layout="isHorizontalLayout"
            :key="modifiedTrader?.id"
            name-error-message=""
            code-error-message=""
            :validations="$v"
            ref="traderDetailsHeaderRef" />
        </PerfectScrollbar>
      </template>

      <template #detailsContent>
        <BaseTable
          table-id="trader-tradeSites"
          :list="traderSites"
          :headers="traderSitesHeaders"
          hide-grouping
          is-add-new-hidden
          @refresh-data="onRefreshTraderSites"></BaseTable>
      </template>
    </AppResizableContainer>

    <BaseDrawerConfirm
      :is-active="confirmModalActive"
      @cancel="confirmModalActive = false"
      @confirm="onDiscardChanges" />
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { deepCopy } from '@/utils/data';
import {
  addOrEditTrader,
  getTraderDetailsById,
  getTraderSitesWithConditions,
} from '@/services/api/traders';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';

import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import AppResizableContainer from '@/components/common/AppResizableContainer.vue';
import TraderDetailsHeader from '@/components/traders/TraderDetailsHeader.vue';
import BaseTable from '@/components/common/BaseTable.vue';

import type { TraderSite, Trader } from '@/types/Traders';
import type { TableFilterData } from '@/types/Base';
import { helpers, required } from '@vuelidate/validators';
import { useToastStore } from '@/stores/toast';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';

const isHorizontalLayout = ref(true);
const route = useRoute();
const trader = ref<Trader>();
const modifiedTrader = ref<Trader>();
const mode = ref<'view' | 'edit'>('view');
const isModified = ref(false);
const traderSites = ref<TraderSite[]>([]);
const confirmModalActive = ref(false);
const isBackMode = ref(false);

watch(
  () => modifiedTrader,
  (newData) => {
    isModified.value = JSON.stringify(newData) !== JSON.stringify(trader.value);
  }
);

onMounted(async () => {
  const res = await getTraderDetailsById(route.params.id as string);
  if (res) {
    trader.value = res as Trader;
    mode.value = 'view';
    traderSites.value = (await getTraderSitesWithConditions({ traderId: trader.value.id })).data;
  } else {
    trader.value = {
      id: route.params.id as string,
      isFromManager: true,
      isActive: true,
      code: '',
      name: '',
    };
    mode.value = 'edit';
  }
  modifiedTrader.value = deepCopy<Trader>(trader.value);
});

const { t: $t } = useI18n();
const traderSitesHeaders = [
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'city', text: $t('city'), type: 'text', active: true },
  { value: 'address', text: $t('address'), type: 'text', active: true },
  { value: 'zipCode', text: $t('zipCodes'), type: 'text', active: true },
  { value: 'telephone1', text: $t('telephone'), type: 'text', active: true },
  { value: 'vatLevel.name', text: $t('vatLevel'), type: 'text', active: true },
  { value: 'isBranch', text: `${$t('branch')}`, type: 'check', active: true },
  { value: 'isDefault', text: `${$t('material.default')}`, type: 'check', active: true },
  { value: 'isHQ', text: `${$t('HQ')}`, type: 'check', active: true },
];

async function onRefreshTraderSites(data: TableFilterData) {
  if (!modifiedTrader.value) return;
  const sortColumn = data.column !== undefined ? traderSitesHeaders[data.column].value : '';
  const res = await getTraderSitesWithConditions({
    itemsPerPage: data.itemsPerPage || 50,
    page: data.page || 1,
    query: data.query,
    sortColumn,
    sortAscending: data.order === 'asc',
    traderId: trader.value?.id,
  });

  if (res) {
    traderSites.value = res.data;
  }
}

const validationModel = ref({
  code: modifiedTrader.value?.code || '',
  name: modifiedTrader.value?.name || '',
  mwCountry: modifiedTrader.value?.mwCountry || '',
  mwAccountGroup: modifiedTrader.value?.mwAccountGroupId || '',
  passportExpiredDate: '',
  adT_date: '',
});

const traderDetailsHeaderRef = ref();

const rules = computed(() => ({
  code: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
  name: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
  mwCountry: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
  mwAccountGroup: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
  passportExpiredDate: {
    validDate: helpers.withMessage($t('date.invalidDate'), () => {
      const pickerRef = traderDetailsHeaderRef.value?.$refs?.passportExpireDatePickerRef;
      const validation = pickerRef?.getValidationState();
      return !validation || validation.isValid;
    }),
  },
  adT_date: {
    validDate: helpers.withMessage($t('date.invalidDate'), () => {
      const pickerRef = traderDetailsHeaderRef.value?.$refs?.adtDatePickerRef;
      const validation = pickerRef?.getValidationState();
      return !validation || validation.isValid;
    }),
  },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

watch(
  () => modifiedTrader.value,
  (newData) => {
    validationModel.value.name = newData?.name || '';
    validationModel.value.code = newData?.code || '';
    validationModel.value.mwAccountGroup = newData?.mwAccountGroupId || '';
    validationModel.value.mwCountry = newData?.mwCountryId || '';

    isModified.value = JSON.stringify(newData) !== JSON.stringify(trader.value);
  },
  { deep: true }
);

const toastStore = useToastStore();
async function onSaveChanges() {
  $v.value.$touch();
  if ($v.value.$invalid) return;

  if (modifiedTrader.value) {
    const res = await addOrEditTrader(modifiedTrader.value);
    res
      ? toastStore.success({ text: `${$t('common.toast.success')}` })
      : toastStore.error({ text: res });

    if (res) {
      mode.value = 'view';
      isModified.value = false;
      trader.value = deepCopy<Trader>(modifiedTrader.value);
    }
  }
}

const router = useRouter();
function onDiscardChanges() {
  mode.value = 'view';
  confirmModalActive.value = false;
  if (trader.value) modifiedTrader.value = deepCopy<Trader>(trader.value);
  if (!trader.value?.createdDate || isBackMode.value) {
    router.push('/traders');
  }
}

function onCancelEdit(backMode: string) {
  if (isModified.value) {
    confirmModalActive.value = true;
    if (backMode == 'back') {
      isBackMode.value = true;
    }
    return;
  } else if (!trader.value?.createdDate) {
    router.push('/customers');
  }
  mode.value = 'view';
}
</script>
